import { create } from "zustand";
import Cookies from "js-cookie";
import config from "../src/config.js";
import {
  buildChatErrorSearchQuestion,
  buildRestoredQuestionState,
} from "./utils/chatQuestion.js";
import {
  getCompletedStatusData,
  parseStatusSSEBlock,
} from "./utils/statusSSE.js";
import { apiFetch } from "./utils/apiClient.js";
import { trackMailEvent } from "./utils/mailAttribution.js";
import {
  getLowActionConflict,
  LowActionConflictError,
} from "./utils/lowAction.js";
import { buildPendingRefinementSituation } from "./utils/refinementDisplay.js";
import { normalizeAnswerOptions } from "./utils/chatChoices.js";
import {
  mergeProgressStep,
  normalizeProgressSteps,
} from "./utils/queryProgress.js";

const SERVER_URI = config.serverUri;
const USER_ISSUE_MAX_LENGTH = 10;
const GUEST_BUSY_RETRY_DELAYS = [3, 5, 8];
let userSessionVersion = 0;

const startSearchStream = async ({
  set,
  get,
  requestBody,
  displayText,
  waitingSituation = null,
  pendingRefinement = null,
  onComplete,
  onStart,
}) => {
  let accepted = false;
  let acceptedReader = null;
  try {
    const response = await apiFetch(`${SERVER_URI}/private/query/stream`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
      },
      body: JSON.stringify(requestBody),
    });

    const conflict = await getLowActionConflict(response);
    if (conflict) {
      set({ lowActionConflict: conflict });
      throw new LowActionConflictError(conflict);
    }
    if (!response.ok) {
      throw new Error(`SSE connection failed: ${response.status}`);
    }

    const contentType = response.headers?.get?.("content-type") || "";
    if (contentType.includes("application/json")) {
      const cached = await response.json();
      if (cached?.log_id) {
        set({
          logId: cached.log_id,
          queryCompleteData: cached,
          isLoading: false,
          isPendingSearchAccepted: false,
        });
        onComplete?.(cached.log_id, cached);
        return { cached: true, logId: cached.log_id };
      }
      throw new Error("cached search response is missing log_id");
    }

    // 서버가 요청을 수락한 뒤에만 이전 화면과 연결을 정리합니다.
    get().clearSSEEvents();
    set({
      isNewChatActive: false,
      tempLog: displayText,
      ...(waitingSituation !== null
        ? {
          textValue: waitingSituation,
          situation: waitingSituation,
        }
        : {}),
      pendingRefinement,
      isPendingSearchAccepted: false,
      isChatMode: false,
      hasActiveChatSession: false,
      chatHistory: [],
      updatedQuestion: "",
      hasUpdatedQuestion: false,
      updatedQuestionHistory: [],
      questionHistoryViewIndex: 0,
      isAIResponding: false,
      isChatDone: false,
      chatSSEData: null,
      chatErrorMessage: "",
      chatSearchFallbackAvailable: false,
      pendingEmptyResponse: false,
      issues: [],
      userIssues: [],
      aiIssues: [],
      sseData: null,
      logId: null,
      sseEventName: undefined,
      sseTitle: "",
      sseContent: "",
      queryCompleteData: null,
    });

    const { sseReader } = get();
    if (sseReader) {
      sseReader.cancel();
    }
    get().cleanupStatusSSE();

    set((state) => ({
      isLoading: true,
      isChatMode: false,
      hasActiveChatSession: false,
      isNewChatActive: false,
      isPendingSearchAccepted: true,
      statusMonitorVersion: state.statusMonitorVersion + 1,
    }));
    accepted = true;
    trackMailEvent("first_search");
    onStart?.();

    const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
    acceptedReader = reader;
    set({ sseReader: reader, isSSEConnected: true });
    while (true) {
      const { done } = await reader.read();
      if (done) break;
    }
    if (get().sseReader === reader) {
      set({ sseReader: null, isSSEConnected: false });
    }
    return { cached: false };
  } catch (error) {
    if (error instanceof LowActionConflictError) {
      return { conflict: true };
    }
    if (accepted) {
      if (acceptedReader && get().sseReader !== acceptedReader) {
        return { accepted: true, staleStream: true };
      }
      console.warn(
        "Accepted query response stream was interrupted; recovering from status snapshot:",
        error,
      );
      set((state) => ({
        sseReader: null,
        isSSEConnected: false,
        isLoading: true,
        isPendingSearchAccepted: true,
        statusMonitorVersion: state.statusMonitorVersion + 1,
      }));
      return { accepted: true, streamInterrupted: true };
    }
    console.error("SSE connection error:", error);
    set({
      isLoading: false,
      isSSEConnected: false,
      tempLog: null,
      isPendingSearchAccepted: false,
      sseEventName: "Error",
      ...(pendingRefinement
        ? {
          textValue: pendingRefinement.baseSituation,
          situation: pendingRefinement.baseSituation,
          pendingRefinement: null,
        }
        : {}),
    });
    get().cleanupSSE();
    return { error };
  }
};

const useStore = create((set, get) => ({
  // temporaryUserInfo: {
  //   createdAt: "2024-04-12T07:30:56Z",
  //   creditAmount: 100,
  //   deletedAt: {
  //     time: "string",
  //     valid: true,
  //   },
  //   email: "test_lawlow.korea@gmail.com",
  //   id: 0,
  //   isDeleted: true,
  //   nickname: "사용자",
  //   oauthInfo: "string",
  //   updatedAt: "string",
  // },

  // 질문중 새로고침을 시도했었는지 여부 저장
  // 로컬 스토리지에 상태 동기화(영구 저장)하여 새로고침을 해도 상태값이 초기화되지 않도록 조치함
  isRefreshDetected: false,
  setRefreshDetected: (value) => {
    localStorage.setItem("isRefreshDetected", JSON.stringify(value));
    set({ isRefreshDetected: value });
  },
  initializeStore: () => {
    const isRefreshDetected = JSON.parse(
      localStorage.getItem("isRefreshDetected")
    );
    if (isRefreshDetected !== null) {
      set({ isRefreshDetected });
    }
  },

  // 로딩 퍼센트값 실시간 저장
  // progress: 0,
  // setProgress: (value) => set({ progress: value }),

  // 사용자 정보
  userInfo: null,
  setUserInfo: (value) => {
    userSessionVersion += 1;
    set(value == null
      ? { userInfo: null, userLawHistory: [], logs: [] }
      : { userInfo: value });
  },

  lowActionConflict: null,
  setLowActionConflict: (value) => set({ lowActionConflict: value }),
  clearLowActionConflict: () => set({ lowActionConflict: null }),

  fetchRehearsal: async (id) => {
    const response = await apiFetch(`${SERVER_URI}/private/rehearsals/${encodeURIComponent(id)}`, { credentials: "include" });
    if (!response.ok) throw Object.assign(new Error("rehearsal lookup failed"), { status: response.status });
    return response.json();
  },

  fetchEvidenceTimeline: async (id) => {
    const response = await apiFetch(`${SERVER_URI}/private/evidence-timelines/${encodeURIComponent(id)}`, { credentials: "include" });
    if (!response.ok) throw Object.assign(new Error("evidence lookup failed"), { status: response.status });
    return response.json();
  },

  generateEvidenceTimeline: async (request) => {
    const response = await apiFetch(`${SERVER_URI}/private/evidence-timelines`, {
      method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify(request),
    });
    const conflict = await getLowActionConflict(response);
    if (conflict) {
      set({ lowActionConflict: conflict });
      throw new LowActionConflictError(conflict);
    }
    if (!response.ok) throw Object.assign(new Error("evidence generation failed"), { status: response.status });
    const data = await response.json();
    if (!data?.id || !Array.isArray(data?.result?.events)) throw new Error("invalid evidence result");
    await get().updateUserInfo();
    return data;
  },

  generateRehearsal: async (request) => {
    const response = await apiFetch(`${SERVER_URI}/private/rehearsals`, {
      method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify(request),
    });
    const conflict = await getLowActionConflict(response);
    if (conflict) {
      set({ lowActionConflict: conflict });
      throw new LowActionConflictError(conflict);
    }
    if (!response.ok) throw Object.assign(new Error("rehearsal generation failed"), { status: response.status });
    const data = await response.json();
    if (!data?.id || !data?.result?.message || !Array.isArray(data.result.coaching) || data.result.coaching.length !== 3) throw new Error("invalid rehearsal result");
    await get().updateUserInfo();
    return data;
  },

  // 모바일 사이드바 상태
  isMobileSidebarOpen: false,
  setMobileSidebarOpen: (value) => set({ isMobileSidebarOpen: value }),

  // 사용자 로우 사용/충전 내역
  userLawHistory: [],
  setUserLawHistory: (value) => set({ userLawHistory: value }),

  // 사용자가 입력한 상황
  // 열어 둔 결과의 문장. 배경 검색의 질문은 tempLog/textValue로 복구한다.
  situation: false,
  setSituation: (value) => set({ situation: value }),

  // 로딩 화면을 띄울지
  loading: false,
  setLoading: (value) => set({ loading: value }),

  // 사용자가 질문한 내용
  textValue: "",
  setTextValue: (value) => set({ textValue: value }),

  // 질문 내역
  logs: [],
  setLogs: (value) => set({ logs: value }),

  // 질문 시도 여부 상태 및 액션
  // 로딩값의 초기화를 위해 질문을 한 번이라도 했을 경우 true로 바꾼다
  hasAttemptedQuestion: false,
  setHasAttemptedQuestion: () => {
    set({ hasAttemptedQuestion: true });
  },

  // 질문 히스토리 삭제 스낵바
  showDeleteHistorySnackbar: false,
  setShowDeleteHistorySnackbar: (value) =>
    set({ showDeleteHistorySnackbar: value }),

  // 복사 완료 스낵바
  showCopyCaseSnackbar: false,
  setShowCopyCaseSnackbar: (value) => set({ showCopyCaseSnackbar: value }),

  // 특정 key의 쿠키 삭제
  deleteCookieByKey: (key) => {
    const cookies = document.cookie.split("; ");
    cookies.forEach((cookie) => {
      const [cookieKey] = cookie.split("=");
      if (cookieKey === key) {
        document.cookie = `${key}=; Max-Age=0; path=/; domain=${window.location.hostname}`;
      }
    });
  },

  // 기본 fetch함수
  fetchPostRequest: (uri, data) => {
    return apiFetch(`${SERVER_URI}${uri}`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    })
      .then((response) => {
        if (!response) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .catch((error) => {
        console.error("Error sending POST request:", error);
        return null;
      });
  },

  // 사용자 정보 업데이트 함수
  updateUserInfo: async (setShowUnauthorizedModal, onAnonymous) => {
    const version = userSessionVersion;
    const hadAuthenticatedSession = Boolean(get().userInfo) || Boolean(Cookies.get("lawlow"));
    const uri = `/private/users/info`;
    try {
      const response = await fetch(`${SERVER_URI}${uri}`, {
        credentials: "include",
      });
      if (version !== userSessionVersion) return;
      if (!response.ok) {
        if (response.status === 401) {
          get().setUserInfo(null);
          if (hadAuthenticatedSession) setShowUnauthorizedModal?.(true);
          else onAnonymous?.();
          return;
        }
        throw new Error(
          `Failed to fetch user info from store : ${response.statusText}`
        );
      }
      const data = await response.json();
      if (version !== userSessionVersion) return;
      set({ userInfo: data }); // info로 요청했던 사용자 정보
    } catch (error) {
      console.error("Failed to fetch user info from store :", error);
    }
  },

  // 사용자 로우 내역 업데이트 함수
  updateUserLaw: async () => {
    const version = userSessionVersion;
    const uri = `/private/users/credit`;
    try {
      const response = await fetch(`${SERVER_URI}${uri}`, {
        credentials: "include",
      });
      if (version !== userSessionVersion) return;
      if (!response.ok) {
        throw new Error(
          `Failed to fetch user Law from store : ${response.statusText}`
        );
      }
      const data = await response.json();
      if (version !== userSessionVersion) return;
      set({ userLawHistory: data });
      return data;
    } catch (error) {
      console.error("Failed to fetch user Law 내역 from store :", error);
    }
  },
  // 질문 내역 요청 함수
  fetchLogs: async () => {
    const version = userSessionVersion;
    const uri = `/private/logs`;
    try {
      const response = await fetch(`${SERVER_URI}${uri}`, {
        credentials: "include",
      });
      if (version !== userSessionVersion) return;

      if (!response.ok) {
        throw new Error(`Failed to fetch logs: ${response.statusText}`);
      }

      const data = await response.json();
      if (version !== userSessionVersion) return;
      set({ logs: data }); // 완료 여부는 status SSE의 terminal event로만 판정한다.
    } catch (error) {
      console.error("Error fetching logs from store:", error);
    }
  },

  // 초대 코드 관련 상태
  referralCode: null,
  setReferralCode: (code) => set({ referralCode: code }),

  // 내 초대 코드 조회/생성 함수
  fetchReferralCode: async () => {
    const uri = `/referral/code`;
    try {
      const response = await fetch(`${SERVER_URI}${uri}`, {
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch referral code: ${response.statusText}`);
      }

      const data = await response.json();
      set({ referralCode: data.code }); // 초대 코드 저장
      return data;
    } catch (error) {
      console.error("Error fetching referral code from store:", error);
      return null;
    }
  },

  // SSE 관련 상태
  sseReader: null,
  sseData: null,
  isSSEConnected: false,
  isLoading: false,
  logId: null,
  sseEventName: undefined,
  sseTitle: "",
  sseContent: "",
  queryCompleteData: null, // Query Complete 이벤트 데이터 저장
  
  setSSEEventName: (eventName) => set({ sseEventName: eventName }),

  // SSE 시작
  startSSE: async (text, onComplete, onStart, options = {}) => {
    const state = get();
    const searchIssues = state.isChatMode ? state.issues : [];
    const sourceLogId = Number(options?.sourceLogId);
    const requestBody = { content: text, issues: searchIssues };
    if (Number.isSafeInteger(sourceLogId) && sourceLogId > 0) {
      requestBody.source_log_id = sourceLogId;
    }
    return startSearchStream({
      set,
      get,
      requestBody,
      displayText: text,
      onComplete,
      onStart,
    });
  },

  startRefinementSSE: async (
    sourceLogId,
    answers,
    requestId,
    onComplete,
    onStart,
    pendingQuestions = [],
  ) => {
    const currentSituation = typeof get().situation === "string"
      ? get().situation.trim()
      : "";
    const waitingSituation = buildPendingRefinementSituation(
      currentSituation,
      pendingQuestions,
    ) || "확인 답변을 반영해 유사 판례를 다시 검색합니다.";

    return startSearchStream({
      set,
      get,
      requestBody: {
        refinement: {
          source_log_id: Number(sourceLogId),
          request_id: requestId,
          answers,
        },
      },
      displayText: waitingSituation,
      waitingSituation,
      pendingRefinement: { baseSituation: currentSituation },
      onComplete,
      onStart,
    });
  },

  // SSE 중지 및 정리
  cleanupSSE: () => {
    const { sseReader } = get();
    if (sseReader) {
      sseReader.cancel();
      set({ 
        sseReader: null, 
        sseData: null,
        isSSEConnected: false,
        isLoading: false,
        logId: null,
        sseEventName: undefined,
        sseTitle: "",
        sseContent: "",
      });
    }
  },

  withdrawUser: async () => {
    const userInfo = get().userInfo;
    if (!userInfo) return false;
    
    try {
      const response = await apiFetch(`${SERVER_URI}/private/users/${userInfo.id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      
      if (!response.ok) {
        throw new Error(`탈퇴 실패: ${response.statusText}`);
      }
      
      // 쿠키 삭제
      get().deleteCookieByKey('accessToken');
      // 사용자 정보 초기화
      get().setUserInfo(null);
      return true;
    } catch (error) {
      console.error('회원탈퇴 실패:', error);
      return false;
    }
  },

  // 로그인 횟수 카운트 API 호출
  trackLogin: async () => {
    try {
      const response = await apiFetch(`${SERVER_URI}/private/users/loginCount`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        return true;
      } else {
        return false;
      }
    } catch (error) {
      return false;
    }
  },

  // 1. 상태 추가
  sseEvents: [],
  addSSEEvent: (event) => set(state => ({
    sseEvents: [...state.sseEvents, event]
  })),
  progressSteps: [],
  progressRequestId: "",
  progressRevision: 0,
  applyProgressEvent: (eventData) => set((state) => {
    const stage = eventData?.stage || eventData?.data?.stage;
    if (!stage) return state;
    const requestId = eventData?.request_id || eventData?.data?.request_id || "";
    const revision = Number(
      eventData?.revision || eventData?.data?.revision || stage?.revision || 0,
    );
    const requestChanged = Boolean(
      requestId && state.progressRequestId && requestId !== state.progressRequestId,
    );
    if (!requestChanged && revision > 0 && revision < state.progressRevision) {
      return state;
    }
    const base = requestChanged ? [] : state.progressSteps;
    const progressSteps = mergeProgressStep(base, stage, revision);
    return {
      progressSteps,
      progressRequestId: requestId || state.progressRequestId,
      progressRevision: Math.max(requestChanged ? 0 : state.progressRevision, revision),
      sseEvents: progressSteps,
    };
  }),
  applyQueryStatusSnapshot: (snapshot) => set((state) => {
    if (!snapshot || typeof snapshot !== "object") return state;
    const requestId = snapshot.request_id || "";
    const revision = Number(snapshot.revision || 0);
    const requestChanged = Boolean(
      requestId && state.progressRequestId && requestId !== state.progressRequestId,
    );
    if (!requestChanged && revision < state.progressRevision) return state;
    const progressSteps = normalizeProgressSteps(snapshot.steps, revision);
    const question = typeof snapshot.question === "string"
      ? snapshot.question.trim()
      : "";
    return {
      progressSteps,
      progressRequestId: requestId || state.progressRequestId,
      progressRevision: revision,
      sseEvents: progressSteps,
      ...(question
        ? {
          tempLog: question,
          textValue: question,
        }
        : {}),
      ...(snapshot.status === "processing"
        ? {
          isLoading: true,
          isPendingSearchAccepted: true,
        }
        : {}),
    };
  }),
  clearSSEEvents: () => set({
    sseEvents: [],
    progressSteps: [],
    progressRequestId: "",
    progressRevision: 0,
  }),

  isChatMode: false,
  setIsChatMode: (value) => set({ isChatMode: value }),
  hasActiveChatSession: false,
  setHasActiveChatSession: (value) => set({ hasActiveChatSession: value }),

  // 대화 기록을 저장할 배열
  chatHistory: [],
  addChatMessage: (message) => set((state) => ({ 
    chatHistory: [...state.chatHistory, { type: 'user', content: message, timestamp: Date.now() }] 
  })),
  addAIResponse: (response, isQuestionUpdate = false, question = {}) => set((state) => ({
    chatHistory: [...state.chatHistory, {
      type: 'ai', content: response, timestamp: Date.now(), isQuestionUpdate,
      questionId: question.question_id || '',
      answerOptions: normalizeAnswerOptions(question.answer_options),
    }]
  })),

  answerChatQuestion: (questionId, answer, { isGuest = false } = {}) => {
    const state = get();
    const question = state.chatHistory[state.chatHistory.length - 1];
    if (!questionId || question?.type !== 'ai' || question.questionId !== questionId ||
        state.isAIResponding || state.isLoading || state.isChatDone || state.chatErrorMessage) {
      return false;
    }
    if (isGuest && state.guestSessionComplete) {
      state.requestGuestLogin('continue');
      return false;
    }
    if (!question.answerOptions?.includes(answer) && answer !== '모르겠어요') return false;
    // 동기적으로 잠근 뒤 기존 대화 경로를 사용한다. 검색·로우 사용은 실행하지 않는다.
    set({ isAIResponding: true, chatErrorMessage: '' });
    state.addChatMessage(answer);
    state.startChatSSE(answer, null, null, { isGuest });
    return true;
  },

  clearChatHistory: () => set({ chatHistory: [] }),

  // 새로운 채팅 SSE 관련 상태
  chatSSEData: null,
  setChatSSEData: (data) => set({ chatSSEData: data }),
  
  // QuestionUpdate로 업데이트된 질문 내용
  updatedQuestion: '',
  hasUpdatedQuestion: false, // QuestionUpdate 이벤트로 질문이 업데이트되었는지 추적
  setUpdatedQuestion: (question) => {
    const normalized = typeof question === 'string' ? question.trim() : '';
    set({
      updatedQuestion: normalized,
      hasUpdatedQuestion: Boolean(normalized),
    });
    // 빈 문자열이 아닌 경우에만 히스토리에 추가
    if (normalized) {
      get().addUpdatedQuestionToHistory(normalized);
    }
  },
  
  // updatedQuestion의 히스토리를 저장할 배열
  updatedQuestionHistory: [],
  /** 카드·히스토리 UI에서 보고 있는 질문 버전 인덱스 (0 .. length-1) */
  questionHistoryViewIndex: 0,
  addUpdatedQuestionToHistory: (question) => {
    set((state) => {
      const currentTime = Date.now();
      const lastItem = state.updatedQuestionHistory[state.updatedQuestionHistory.length - 1];
      
      // 서버에서 QuestionUpdate와 Error 이벤트를 둘 다 보낼 때 같은 메시지가 두 번 저장되는 것을 방지하기 위해 중복 체크 로직을 추가 (26.01.27 수정)
      // 중복 체크: 마지막 항목과 내용이 같고 시간 차이가 2초 미만이면 추가하지 않음
      if (lastItem && 
          lastItem.content === question && 
          (currentTime - lastItem.timestamp) < 2000) {
        return state; // 중복이므로 추가하지 않음
      }
      
      const newHistory = [...state.updatedQuestionHistory, { content: question, timestamp: currentTime }];
      return {
        updatedQuestionHistory: newHistory,
        questionHistoryViewIndex: newHistory.length - 1,
      };
    });
  },
  clearUpdatedQuestionHistory: () => set({ updatedQuestionHistory: [], questionHistoryViewIndex: 0 }),
  goQuestionHistoryBack: () =>
    set((state) => ({
      questionHistoryViewIndex: Math.max(0, state.questionHistoryViewIndex - 1),
    })),
  goQuestionHistoryForward: () =>
    set((state) => {
      const len = state.updatedQuestionHistory.length;
      if (len === 0) return state;
      const maxIdx = len - 1;
      return {
        questionHistoryViewIndex: Math.min(maxIdx, state.questionHistoryViewIndex + 1),
      };
    }),
  
  // AI 응답 로딩 상태
  isAIResponding: false,
  setIsAIResponding: (value) => set({ isAIResponding: value }),
  
  // 채팅 완료 상태
  isChatDone: false,
  setIsChatDone: (value) => set({ isChatDone: value }),

  // 채팅 에러 상태
  chatErrorMessage: '',
  chatSearchFallbackAvailable: false,
  setChatErrorMessage: (message) => set({
    chatErrorMessage: message,
    ...(!message ? { chatSearchFallbackAvailable: false } : {}),
  }),

  // ChatResponse가 비어있는 상태 (ChatDoneCheck 대기용)
  pendingEmptyResponse: false,
  setPendingEmptyResponse: (value) => set({ pendingEmptyResponse: value }),

  // 비로그인 채팅 상태는 새로고침 시 복원하지 않고 메모리에만 유지한다.
  guestHandoffToken: "",
  guestRequestUsed: false,
  guestSessionComplete: false,
  guestAvailabilityStatus: "unknown",
  guestResponseComplete: false,
  guestLoginReason: "",
  guestQueueMessage: "",
  requestGuestLogin: (reason = "continue") => set({ guestLoginReason: reason }),
  closeGuestLogin: () => set({ guestLoginReason: "" }),
  fetchGuestChatAvailability: async () => {
    set({ guestAvailabilityStatus: "checking" });
    try {
      const response = await fetch(`${SERVER_URI}/query/chat/availability`, {
        method: "GET",
        credentials: "include",
        headers: {
          "Accept": "application/json",
        },
      });
      if (!response.ok) {
        set({ guestAvailabilityStatus: "error" });
        return null;
      }
      const data = await response.json();
      const available = data.available === true;
      set({
        guestAvailabilityStatus: available ? "available" : "used",
        guestRequestUsed: !available,
        guestSessionComplete: !available,
      });
      return available;
    } catch (error) {
      console.error("비로그인 대화 사용 가능 여부 조회 실패:", error);
      set({ guestAvailabilityStatus: "error" });
      return null;
    }
  },
  syncGuestIssues: async () => {
    const {
      guestHandoffToken,
      issues,
      userIssues,
    } = get();
    if (!guestHandoffToken) return true;

    try {
      const response = await fetch(`${SERVER_URI}/query/chat/issues`, {
        method: "PATCH",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
        body: JSON.stringify({
          handoff_token: guestHandoffToken,
          issues,
          user_issues: userIssues,
        }),
      });
      if (!response.ok) {
        if (response.status === 404) {
          const result = await response.json().catch(() => null);
          if (get().guestHandoffToken === guestHandoffToken && result?.updated === false &&
            ["no_guest_session", "guest_session_expired"].includes(result.status)) {
            return { updated: false, status: result.status };
          }
        }
        return false;
      }

      const data = await response.json();
      if (data.updated !== true) return false;
      set({
        issues: data.issues || [],
        userIssues: data.user_issues || [],
        aiIssues: data.ai_issues || [],
      });
      return true;
    } catch (error) {
      console.error("비로그인 법률 쟁점 동기화 실패:", error);
      return false;
    }
  },
  resetGuestChatState: () => {
    const { sseReader } = get();
    sseReader?.cancel?.();
    set({
      chatHistory: [],
      updatedQuestion: "",
      hasUpdatedQuestion: false,
      updatedQuestionHistory: [],
      questionHistoryViewIndex: 0,
      isAIResponding: false,
      isChatDone: false,
      isChatMode: false,
      chatSSEData: null,
      chatErrorMessage: "",
      chatSearchFallbackAvailable: false,
      pendingEmptyResponse: false,
      isNewChatActive: false,
      hasActiveChatSession: false,
      issues: [],
      userIssues: [],
      aiIssues: [],
      guestHandoffToken: "",
      guestRequestUsed: false,
      guestSessionComplete: false,
      guestAvailabilityStatus: "unknown",
      guestResponseComplete: false,
      guestLoginReason: "",
      guestQueueMessage: "",
      sseReader: null,
      isSSEConnected: false,
    });
  },

  // 쟁점 관련 상태 (AI 제안 + 사용자 직접 입력, 최대 3개 / AI 최대 2개)
  issues: [],
  userIssues: [],
  aiIssues: [],
  addUserIssue: (issue) => {
    const { issues, userIssues } = get();
    const rawIssue = typeof issue === 'string' ? issue : String(issue ?? '');
    const trimmed = rawIssue.trim();
    // 사용자 직접 입력만 공백 포함 10자 제한을 적용하고, AI 쟁점은 서버 응답 그대로 유지한다.
    if (
      !trimmed ||
      rawIssue.length > USER_ISSUE_MAX_LENGTH ||
      issues.length >= 3 ||
      issues.includes(trimmed)
    ) return;
    set({
      issues: [...issues, trimmed],
      userIssues: [...userIssues, trimmed],
    });
  },
  removeIssue: (issue) => {
    const { issues, userIssues, aiIssues } = get();
    set({
      issues: issues.filter(i => i !== issue),
      userIssues: userIssues.filter(i => i !== issue),
      aiIssues: aiIssues.filter(i => i !== issue),
    });
  },

  // 채팅 리셋 API (무료/기본)
  resetChat: async () => {
    try {
      
      const response = await apiFetch(`${SERVER_URI}/private/query/chat/reset`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        // console.error('채팅 리셋 요청 실패:', response.status, response.statusText);
        throw new Error(`Chat reset failed: ${response.status} ${response.statusText}`);
      }

      
      // 채팅 관련 상태 초기화
      set({ 
        chatHistory: [],
        updatedQuestion: '',
        hasUpdatedQuestion: false,
        updatedQuestionHistory: [],
        questionHistoryViewIndex: 0,
        isAIResponding: false,
        isChatDone: false,
        isChatMode: false,
        chatSSEData: null,
        chatErrorMessage: '',
        chatSearchFallbackAvailable: false,
        pendingEmptyResponse: false,
        isNewChatActive: false,
        hasActiveChatSession: false,
        issues: [],
        userIssues: [],
        aiIssues: [],
      });

      return true;
    } catch (error) {
      // console.error('채팅 리셋 에러:', error);
      return false;
    }
  },

  // 채팅 리셋 API (유료 - 5로우 차감)
  resetChatPaid: async ({ preserveLocalState = false } = {}) => {
    try {
      const response = await apiFetch(`${SERVER_URI}/private/query/chat/reset/paid`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      const conflict = await getLowActionConflict(response);
      if (conflict) {
        set({ lowActionConflict: conflict });
        throw new LowActionConflictError(conflict);
      }
      if (!response.ok) {
        if (response.status === 400) {
          throw new Error('크레딧이 부족합니다');
        } else if (response.status === 401) {
          throw new Error('사용자 인증이 필요합니다');
        }
        throw new Error(`Chat reset paid failed: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      
      if (!preserveLocalState) {
        // 채팅 관련 상태 초기화
        set({ 
          chatHistory: [],
          updatedQuestion: '',
          hasUpdatedQuestion: false,
          updatedQuestionHistory: [],
          questionHistoryViewIndex: 0,
          isAIResponding: false,
          isChatDone: false,
          isChatMode: false,
          chatSSEData: null,
          chatErrorMessage: '',
          chatSearchFallbackAvailable: false,
          pendingEmptyResponse: false,
          isNewChatActive: false,
          hasActiveChatSession: false,
          issues: [],
          userIssues: [],
          aiIssues: [],
        });
      }

      return data;
    } catch (error) {
      if (!(error instanceof LowActionConflictError)) {
        console.error('채팅 유료 리셋 에러:', error);
      }
      throw error;
    }
  },

  // 대화 히스토리 불러오기 API
  getChatHistory: async () => {
    try {
      
      const response = await fetch(`${SERVER_URI}/private/query/chat/history`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        console.error('대화 히스토리 요청 실패:', response.status, response.statusText);
        throw new Error(`Chat history failed: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      set({ hasActiveChatSession: data?.isChat === true });
      return data;
    } catch (error) {
      console.error('대화 히스토리 에러:', error);
      return { isChat: false, Question: "", Chat: null, requestFailed: true };
    }
  },

  // 대화 히스토리 복원
  restoreChatHistory: (historyData) => {
    try {
      const {
        Question,
        Chat,
        is_chat_done,
        issues = [],
        user_issues: userIssues = [],
        ai_issues: responseAIIssues,
      } = historyData;
      const normalizedIssues = Array.isArray(issues) ? issues : [];
      const normalizedUserIssues = Array.isArray(userIssues) ? userIssues : [];
      const normalizedAIIssues = Array.isArray(responseAIIssues)
        ? responseAIIssues
        : normalizedIssues.filter(issue => !normalizedUserIssues.includes(issue));
      const allMessages = [];

      // Client와 Server 메시지들을 합쳐서 index 순서로 정렬
      if (Chat?.Client) {
        Chat.Client.forEach(msg => {
          allMessages.push({
            type: 'user',
            content: msg.content, // 소문자로 수정
            index: msg.index,
            timestamp: Date.now()
          });
        });
      }

      if (Chat?.Server) {
        Chat.Server.forEach(msg => {
          // content가 비어있지 않은 경우만 추가
          if (msg.content && msg.content.trim() !== '') {
            allMessages.push({
              type: 'ai', 
              content: msg.content, // 소문자로 수정
              index: msg.index,
              timestamp: Date.now(),
              questionId: msg.question_id || '',
              answerOptions: normalizeAnswerOptions(msg.answer_options),
            });
          }
        });
      }

      // is_chat_done이 true인 경우 완료 메시지 추가
      if (is_chat_done === true) {
        // 완료 메시지를 히스토리에 추가
        allMessages.push({
          type: 'ai',
          content: '이제 검색 준비가 완료되었습니다! 검색을 진행할까요?',
          index: allMessages.length + 1,
          timestamp: Date.now()
        });
      }

      // index 순서로 정렬
      allMessages.sort((a, b) => a.index - b.index);

      // 상태 업데이트
      set({
        chatHistory: allMessages,
        isChatMode: true,
        hasActiveChatSession: true,
        isNewChatActive: true,
        ...buildRestoredQuestionState(Question),
        chatSearchFallbackAvailable: false,
        issues: normalizedIssues,
        userIssues: normalizedUserIssues,
        aiIssues: normalizedAIIssues,
        isAIResponding: false,
        isChatDone: is_chat_done === true // is_chat_done 값으로 설정
      });

      return true;
    } catch (error) {
      return false;
    }
  },

  // 새로운 채팅 SSE 시작. guest 옵션일 때는 public API와 별도 과부하 재시도를 사용한다.
  startChatSSE: async (text, onComplete, onStart, options = {}) => {
    const isGuest = options.isGuest === true;
    const retryAttempt = options.retryAttempt || 0;
    try {
      // 기존 SSE 연결이 있다면 정리
      const { sseReader } = get();
      if (sseReader) {
        sseReader.cancel();
      }

      const shouldResetIssues = !isGuest && get().isChatDone;
      const pinnedIssues = shouldResetIssues ? [] : get().userIssues;

      set({
        isChatDone: false,
        pendingEmptyResponse: false,
        chatErrorMessage: '',
        chatSearchFallbackAvailable: false,
        guestQueueMessage: "",
        ...(shouldResetIssues ? {
          issues: [],
          userIssues: [],
          aiIssues: [],
        } : {}),
      });

      const request = isGuest ? fetch : apiFetch;
      const endpoint = isGuest ? "/query/chat" : "/private/query/chat";
      const response = await request(`${SERVER_URI}${endpoint}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream'
        },
        body: JSON.stringify({
          message: text,
          issues: pinnedIssues,
          is_issue_pinned: pinnedIssues.length > 0,
        })
      });

      if (!response.ok) {
        if (isGuest && (response.status === 401 || response.status === 429)) {
          const data = await response.json().catch(() => ({}));
          if (data.login_required) {
            const isSearchOnlyRequest =
              data.code === "guest_search_requires_login";
            set({
              isLoading: false,
              isAIResponding: false,
              guestRequestUsed: isSearchOnlyRequest
                ? get().guestRequestUsed
                : true,
              guestAvailabilityStatus: isSearchOnlyRequest
                ? get().guestAvailabilityStatus
                : "used",
              guestSessionComplete: isSearchOnlyRequest
                ? get().guestSessionComplete
                : true,
              guestResponseComplete: true,
              guestLoginReason: isSearchOnlyRequest ? "search" : "continue",
              guestQueueMessage: "",
            });
            return;
          }
          if (
            response.status === 429 &&
            data.code === "guest_busy" &&
            retryAttempt < GUEST_BUSY_RETRY_DELAYS.length
          ) {
            const retryAfter = Number(
              data.retry_after_seconds || response.headers.get("Retry-After"),
            );
            const delaySeconds = Number.isFinite(retryAfter) && retryAfter > 0
              ? retryAfter
              : GUEST_BUSY_RETRY_DELAYS[retryAttempt];
            set({
              isLoading: true,
              isAIResponding: true,
              guestQueueMessage: `현재 이용자가 많아 대기 중입니다. ${delaySeconds}초 후 다시 연결합니다.`,
            });
            await new Promise((resolve) => window.setTimeout(resolve, delaySeconds * 1000));
            if (!get().isAIResponding) {
              set({ isLoading: false, guestQueueMessage: "" });
              return;
            }
            return get().startChatSSE(text, onComplete, onStart, {
              ...options,
              retryAttempt: retryAttempt + 1,
            });
          }

          if (response.status === 429) {
            set({
              isLoading: false,
              isAIResponding: false,
              guestQueueMessage: "",
              chatErrorMessage: "현재 이용자가 많습니다. 잠시 후 다시 시도해주세요.",
              chatSearchFallbackAvailable: Boolean(
                buildChatErrorSearchQuestion(get()),
              ),
            });
            return;
          }
        }
        throw new Error('Chat SSE connection failed');
      }

      onStart?.();
      set({
        isLoading: true,
        isAIResponding: true,
        hasActiveChatSession: isGuest ? false : true,
        isNewChatActive: true,
        guestRequestUsed: isGuest ? true : get().guestRequestUsed,
        guestAvailabilityStatus: isGuest ? "used" : get().guestAvailabilityStatus,
        guestResponseComplete: isGuest ? false : get().guestResponseComplete,
        guestQueueMessage: "",
      });

      const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
      set({ sseReader: reader, isSSEConnected: true });

      let buffer = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += value.toString();

        // 여러 이벤트가 한 번에 올 수 있으니, \n\n(이벤트 구분)로 쪼갬
        const events = buffer.split('\n\n');
        // 마지막은 아직 덜 온 데이터일 수 있으니 버퍼에 남김
        buffer = events.pop();

        for (const event of events) {
          const lines = event.split('\n');
          let eventType = '';
          let eventData = null;
          
          for (const line of lines) {
            if (line.startsWith('event: ')) {
              eventType = line.slice(7);
            } else if (line.startsWith('data: ')) {
              try {
                eventData = JSON.parse(line.slice(6));
              } catch (e) {
                console.error('JSON 파싱 에러:', e);
                continue;
              }
            }
          }

          if (eventType && eventData) {
            set({ chatSSEData: eventData });
            get().handleChatSSEEvent(eventType, eventData, onComplete, { isGuest });
          }
        }
      }
      if (isGuest) {
        set({
          guestResponseComplete: true,
          isLoading: false,
          isAIResponding: false,
        });
      }
    } catch (error) {
      console.error('Chat SSE 에러:', error);
      const searchQuestion = buildChatErrorSearchQuestion(get());
      if (searchQuestion) {
        get().setUpdatedQuestion(searchQuestion);
      }
      set({
        isLoading: false,
        isAIResponding: false,
        chatErrorMessage: '대화 답변 연결이 끊어졌습니다. 현재 내용으로 판례를 검색할 수 있습니다.',
        chatSearchFallbackAvailable: Boolean(searchQuestion),
        ...(isGuest && get().guestRequestUsed
          ? { guestResponseComplete: true }
          : {}),
      });
    }
  },

  // 채팅 SSE 이벤트 처리
  handleChatSSEEvent: (eventType, eventData, onComplete, options = {}) => {
    const isGuest = options.isGuest === true;
    const { addAIResponse, setUpdatedQuestion, setIsAIResponding, setIsChatDone } = get();
    
    switch (eventType) {
      case 'NonsenseCheck':
        // is_nonsense 값에 관계없이 NonsenseCheck 이벤트가 오면 무조건 다음 단계 진행
        break;

      case 'QuestionUpdate':
        const updatedMessage = eventData.message;
        setUpdatedQuestion(updatedMessage);
        break;

      case 'IssuesUpdate':
        // AI 제안 쟁점 업데이트 (사용자가 핀고정한 쟁점은 user_issues로 유지됨)
        set({
          issues: eventData.issues || [],
          userIssues: eventData.user_issues || [],
          aiIssues: eventData.ai_issues || [],
        });
        break;

      case 'ChatResponse':
        // result_code 체크 또는 바로 description 사용
        const aiResponse = eventData.description || eventData.message;
        if (aiResponse && aiResponse.trim() !== '') {
          // ChatResponse의 응답이 잘 왔을 때 
          addAIResponse(aiResponse, false, eventData);
          setIsAIResponding(false);
        } else {
          // ChatResponse의 응답 데이터가 비어있는 경우
          // ChatDoneCheck에서 is_chat_done을 확인한 후 재요청 여부 결정
          const { setPendingEmptyResponse } = get();
          setPendingEmptyResponse(true);
          setIsAIResponding(false);
        }
        break;

      case 'ChatDoneCheck':
        // result_code 체크 또는 바로 description 사용
        const isDone = eventData.description?.is_chat_done || 
                      eventData.is_chat_done || 
                      false;
        
        const { pendingEmptyResponse, setPendingEmptyResponse } = get();
        if (isGuest && eventData.guest_session_complete === true) {
          set({ guestSessionComplete: true });
        }
        
        if (pendingEmptyResponse) {
          // ChatResponse가 비어있었고 ChatDoneCheck 결과에 따라 처리
          if (isDone) {
            // 채팅 완료 - 완료 메시지와 검색 버튼 표시
            addAIResponse('이제 검색 준비가 완료되었습니다! 검색을 진행할까요?');
            setIsChatDone(true);
          } else {
            // 채팅 미완료 - 재요청 실행
            const { updatedQuestion } = get();
            if (updatedQuestion && !isGuest) {
              // 현재 연결 정리
              const { sseReader } = get();
              if (sseReader) {
                sseReader.cancel();
              }
              
              // 재요청 실행
              setTimeout(() => {
                get().startChatSSE(updatedQuestion, onComplete, () => {
                });
              }, 1000);
            } else {
              addAIResponse(
                isGuest
                  ? '답변을 이어서 받으려면 로그인해주세요.'
                  : '답변 생성 중 오류가 발생했습니다. 다시 시도해주세요.',
              );
              if (isGuest) set({ guestLoginReason: "continue" });
            }
          }
          setPendingEmptyResponse(false);
        } else {
          // 정상적인 ChatDoneCheck 처리
          if (isDone) {
            // 채팅 완료 시 완료 메시지 추가
            addAIResponse('이제 검색 준비가 완료되었습니다! 검색을 진행할까요?');
          }
          setIsChatDone(isDone);
        }
        
        set({ isLoading: false });
        
        if (onComplete && eventData.q_id) {
          onComplete(eventData.q_id);
        }
        break;

      case 'Error':
        // 에러 메시지 처리 (서버에서 받은 error 메시지 사용)
        const { setChatErrorMessage } = get();
        const errorMessage = eventData.error || '질문 횟수가 초과되었습니다.';
        setChatErrorMessage(errorMessage);
        
        const searchQuestion = buildChatErrorSearchQuestion(
          get(),
          eventData.search_question,
        );
        if (searchQuestion) {
          setUpdatedQuestion(searchQuestion);
        }
        
        set({
          isLoading: false,
          isAIResponding: false,
          chatSearchFallbackAvailable:
            eventData.can_search === false ? false : Boolean(searchQuestion),
        });
        break;

      case 'HandoffToken':
        set({
          guestHandoffToken:
            eventData.handoff_token || eventData.description?.handoff_token || "",
          guestResponseComplete: true,
        });
        break;

      default:
    }
  },

  // Status Stream SSE 관련 상태
  statusSSEReader: null,
  statusSSEController: null,
  isStatusSSEConnected: false,

  fetchQueryStatus: async () => {
    const response = await apiFetch(`${SERVER_URI}/private/query/status`, {
      method: "GET",
      credentials: "include",
      headers: { Accept: "application/json" },
    });
    if (!response.ok) {
      const error = new Error(`Query status failed: ${response.status}`);
      error.retryable = response.status >= 500;
      throw error;
    }
    const snapshot = await response.json();
    get().applyQueryStatusSnapshot(snapshot);
    return snapshot;
  },

  // Status Stream SSE 시작. 라우트 이동과 무관하게 전역 monitor가 한 연결만 유지한다.
  startStatusSSE: async ({
    onComplete,
    onStart,
    onNoSession,
    onError,
  } = {}) => {
    if (get().statusSSEController) {
      return { reused: true };
    }
    const controller = new AbortController();
    set({ statusSSEController: controller });

    try {
      const response = await fetch(`${SERVER_URI}/private/query/status/stream`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': 'text/event-stream'
        },
        signal: controller.signal
      });

      if (!response.ok) {
        const responseError = new Error(`Status SSE connection failed: ${response.status}`);
        responseError.retryable = response.status >= 500;
        throw responseError;
      }

      onStart?.();
      set({ isLoading: true });

      const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
      if (get().statusSSEController !== controller) {
        await reader.cancel();
        return;
      }

      set({
        statusSSEReader: reader,
        isStatusSSEConnected: true,
      });

      let buffer = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) {
          break;
        }

        buffer += value.toString();

        const events = buffer.split('\n\n');
        buffer = events.pop();

        for (const event of events) {
          let parsedEvent;
          try {
            parsedEvent = parseStatusSSEBlock(event);
          } catch (parseError) {
            console.error('Status SSE JSON 파싱 에러:', parseError, event);
            continue;
          }

          if (!parsedEvent) continue;

          const { eventType, data: eventData } = parsedEvent;
          let normalizedEventType = eventType.toLowerCase();
          const completedStatus = getCompletedStatusData(eventType, eventData);

          set({
            sseData: eventData,
            ...(normalizedEventType === "snapshot"
              ? {}
              : { sseEventName: eventType }),
          });

          if (normalizedEventType === "snapshot") {
            get().applyQueryStatusSnapshot(eventData);
            if (eventData?.status === "processing") continue;
            if (eventData?.status === "none") normalizedEventType = "no_session";
            if (eventData?.status === "error") normalizedEventType = "error";
          }

          get().applyProgressEvent(eventData);

          if (normalizedEventType === 'terminated') {
            return;
          }

          if (normalizedEventType === 'processing') {
            const processingQuestion = typeof eventData?.question === 'string'
              ? eventData.question.trim()
              : '';
            set({
              ...(processingQuestion
                ? {
                  tempLog: processingQuestion,
                  textValue: processingQuestion,
                }
                : {}),
              isPendingSearchAccepted: true,
            });
            continue;
          }

          if (normalizedEventType === 'no_session') {
            await get().fetchLogs();
            const shouldRetry = (await onNoSession?.()) === true;
            const pending = get().pendingRefinement;
            set({
              ...(shouldRetry ? {} : { tempLog: null }),
              ...(!shouldRetry && pending
                ? {
                  textValue: pending.baseSituation,
                  pendingRefinement: null,
                }
                : {}),
              isLoading: false,
              isPendingSearchAccepted: shouldRetry,
            });
            get().cleanupStatusSSE();
            return;
          }

          if (normalizedEventType === 'error') {
            const pending = get().pendingRefinement;
            set({
              tempLog: null,
              isLoading: false,
              isPendingSearchAccepted: false,
              sseEventName: 'Error',
              ...(pending
                ? {
                  textValue: pending.baseSituation,
                  pendingRefinement: null,
                }
                : {}),
            });
            onError?.(eventData, { retryable: false });
            get().cleanupStatusSSE();
            return;
          }

          if (completedStatus?.logId) {
            const { logId, completeData } = completedStatus;
            set({
              logId,
              queryCompleteData: completeData,
              tempLog: null,
              pendingRefinement: null,
              isLoading: false,
              isPendingSearchAccepted: false,
            });
            await get().fetchLogs();
            onComplete?.(logId, completeData);
            get().cleanupStatusSSE();
            return;
          }

          if (
            !eventData?.stage
            && !['recommendedquestionscomplete', 'query complete'].includes(normalizedEventType)
          ) {
            const title = eventData?.description?.title;
            const content = eventData?.description?.content;

            if (title || content) {
              get().addSSEEvent({
                title: title || '',
                content: content || '',
                template_name: eventType,
              });
            }
          } else if (eventData?.stage) {
            const { title = "", content = "" } = eventData.stage;
            set({
              sseTitle: title || "판례 처리중..",
              sseContent: content || "관련 판례를 찾아 처리하고 있어요",
            });
          }
        }
      }

      if (get().statusSSEController === controller) {
        const streamEndedError = new Error(
          'Status SSE stream ended before a terminal event',
        );
        set({
          statusSSEController: null,
          statusSSEReader: null,
          isStatusSSEConnected: false,
        });
        onError?.(streamEndedError, { retryable: true });
      }
    } catch (error) {
      if (error?.name === 'AbortError') return;

      const retryable = error?.retryable !== false;
      if (retryable) {
        console.warn('Status SSE connection interrupted. Retrying:', error);
        set({
          statusSSEController: null,
          statusSSEReader: null,
          isStatusSSEConnected: false,
        });
        onError?.(error, { retryable: true });
        return;
      }

      console.error('Status SSE connection error:', error);
      const pending = get().pendingRefinement;
      set({
        tempLog: null,
        isLoading: false,
        isStatusSSEConnected: false,
        isPendingSearchAccepted: false,
        sseEventName: 'Error',
        ...(pending
          ? {
            textValue: pending.baseSituation,
            pendingRefinement: null,
          }
          : {}),
      });
      onError?.(error, { retryable: false });
      get().cleanupStatusSSE();
    }
  },

  // Status SSE 중지 및 정리
  cleanupStatusSSE: () => {
    const { statusSSEController, statusSSEReader } = get();
    statusSSEController?.abort();
    statusSSEReader?.cancel().catch(() => {});
    set({
      statusSSEController: null,
      statusSSEReader: null,
      isStatusSSEConnected: false,
      isLoading: false,
    });
  },

  // "새 대화 중" 상태 관리
  isNewChatActive: false,
  setIsNewChatActive: (value) => set({ isNewChatActive: value }),
  
  // 채팅 종료 시 "새 대화 중" 상태 비활성화
  deactivateNewChat: () => {
    set({ isNewChatActive: false });
  },

  // SSE Query Complete 이벤트 중 임시 로그 (shimmer 효과로 표시될 임시 항목)
  tempLog: null,
  pendingRefinement: null,
  setTempLog: (log) => set({ tempLog: log }),
  clearTempLog: () => set({ tempLog: null }),
  isPendingSearchAccepted: false,
  statusMonitorVersion: 0,
}));

export default useStore;
